import { Link } from "react-router-dom";
import { ArrowLeft, Sparkle } from "@phosphor-icons/react";
import ListenTranslateBar from "@/components/ListenTranslateBar";
import ShareButton from "@/components/ShareButton";
import FarmersNav from "@/components/FarmersNav";

/**
 * FarmersAbout — the full editorial article introducing Farmers Pulse.
 * Rendered at /farmers/about.
 */
export default function FarmersAbout() {
  const summary =
    "Farmers Pulse — a worldwide platform where farmers, growers, gardeners, agricultural experts, researchers and communities can observe, document, learn and share what is happening on the land around them.";

  return (
    <main
      className="bg-[color:var(--np-bg,#F5EBDC)] text-[#1B4332] min-h-screen pt-32 md:pt-36 pb-32"
      data-testid="farmers-about-page"
    >
      <FarmersNav />
      <div className="max-w-[820px] mx-auto px-5 md:px-8 mt-8">
        <Link
          to="/farmers"
          className="inline-flex items-center gap-1.5 font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[#3A3830] hover:text-[#1B4332]"
        >
          <ArrowLeft size={11} weight="bold" /> Farmers Pulse
        </Link>

        {/* Hero */}
        <header className="mt-4">
          <div className="font-mono-tech text-[10px] tracking-[0.4em] uppercase text-[color:var(--np-brand,#B85F2A)] flex items-center gap-2">
            <Sparkle size={12} weight="fill" /> / Editorial · Farmers Pulse
          </div>
          <h1 className="mt-3 font-serif-display text-4xl md:text-5xl lg:text-6xl leading-[0.98] tracking-tight">
            Farmers Pulse
          </h1>
          <p className="mt-3 font-serif-display italic text-xl md:text-2xl text-[#3A3830] leading-snug">
            Listening to the Land. Learning from Each Other. Growing for the Future.
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <ListenTranslateBar text={summary} testidPrefix="farmers-about-tools" />
            <ShareButton
              title="Farmers Pulse — NaturePulse"
              text="A worldwide platform for farmer observations and community knowledge."
              hashtags={["FarmersPulse", "NaturePulse"]}
              variant="chip"
              testid="farmers-about-share"
            />
          </div>
        </header>

        {/* Article body */}
        <article
          data-testid="farmers-about-body"
          className="mt-8 space-y-6 font-serif-display text-base md:text-lg leading-relaxed text-[#2A2724]"
        >
          <p className="italic text-[#3A3830]">Every farmer watches nature.</p>
          <p>
            The arrival of rain, the condition of the soil, the first flower on a plant, the appearance of an
            unfamiliar insect, the level of water in a well, the behavior of birds, an unusually hot
            afternoon, or the timing of a harvest can all tell a story.
          </p>
          <p>
            For generations, farmers have learned to read these signals. Today, as weather patterns shift and
            agriculture faces new challenges, these local observations are more valuable than ever.
          </p>
          <p>
            Farmers Pulse, a part of NaturePulse, is being created as a worldwide platform where farmers,
            growers, gardeners, agricultural experts, researchers and communities can observe, document, learn
            and share what is happening on the land around them.
          </p>

          <SectionHead>Every Farm Has a Story to Tell</SectionHead>
          <p>
            A farmer doesn't need sophisticated scientific equipment to contribute valuable information.
          </p>
          <p>
            A photograph of a flowering mango tree, the date rice was planted, an unusually dry field, the
            arrival of migratory birds, declining water in a pond, a new crop disease, an early harvest or an
            unexpected rainfall can become an important observation.
          </p>
          <p>Farmers Pulse encourages farmers to document these everyday changes.</p>
          <p>
            Over time, thousands—or eventually millions—of individual observations can help create a living
            record of agriculture and our changing environment.
          </p>

          <SectionHead>Your Local Farming Almanac</SectionHead>
          <p>
            Traditional farming almanacs have helped generations of farmers understand seasons, weather and
            planting cycles.
          </p>
          <p>Farmers Pulse takes that idea into the digital age.</p>
          <p>Depending on location, farmers can access and contribute information about:</p>
          <BulletList
            items={[
              "Weather, rainfall and seasonal conditions",
              "Planting and harvesting calendars",
              "Crops, fruits, vegetables, grains, herbs and spices",
              "Soil health and land conditions",
              "Water availability and irrigation",
              "Pests, weeds and crop diseases",
              "Livestock, poultry and aquaculture",
              "Pollinators, birds and beneficial insects",
              "Drought, flooding, frost, heat and storms",
              "Local crop conditions and harvest observations",
              "Agricultural markets and prices",
              "Farming techniques and technology",
              "Research and agricultural innovation",
              "Government programs, assistance and alerts",
            ]}
          />
          <p>
            The goal is simple: give farmers useful information while allowing farmers themselves to become an
            important source of local knowledge.
          </p>

          <SectionHead>From Your Farm to a Global Map</SectionHead>
          <p>Agriculture is global, but farming is intensely local.</p>
          <p>
            Conditions affecting a rice farmer in Kerala may be completely different from those affecting a
            wheat farmer in Punjab, a coffee farmer in Colombia, a maize farmer in Kenya or an apple grower in
            Oregon.
          </p>
          <p>That is why Farmers Pulse is designed around geography.</p>
          <p>
            Observations can be associated with a country, state or province, district or county, city, town,
            village and local area.
          </p>
          <p>As participation grows, Farmers Pulse can help visualize what farmers are observing across regions.</p>
          <p>Imagine opening a map and seeing:</p>
          <BulletList
            items={[
              "Where is rice being planted this week?",
              "Where are mango trees flowering early?",
              "Where are farmers reporting unusually low rainfall?",
              "Where is a particular pest appearing?",
              "Which areas are experiencing flooding?",
              "Where has harvesting begun?",
            ]}
          />
          <p>
            These individual observations can collectively reveal patterns that might otherwise remain
            unnoticed.
          </p>

          <SectionHead>Photograph It. Document It. Share It.</SectionHead>
          <p>A smartphone can become a powerful agricultural observation tool.</p>
          <p>
            Farmers can photograph a plant, crop, insect, disease, animal, field, water source or weather
            condition and add information such as the location, date, crop, condition and personal
            observations.
          </p>
          <p>
            Artificial intelligence can help organize the information by suggesting identification, titles,
            descriptions, categories and tags.
          </p>
          <p>
            But Farmers Pulse is not intended to replace the farmer's knowledge. AI should assist farmers and
            experts—not silence the experience of people who work with the land every day.
          </p>

          <SectionHead>Ask Farmers Pulse</SectionHead>
          <p>We envision Farmers Pulse AI becoming a practical farming assistant.</p>
          <p>A farmer might photograph a leaf and ask:</p>
          <blockquote className="border-l-4 border-[color:var(--np-brand,#B85F2A)] pl-4 italic text-[#3A3830]">
            "What could be causing these spots?"
          </blockquote>
          <p>Or ask:</p>
          <BulletList
            items={[
              "\u201CWhat vegetables can I plant here this month?\u201D",
              "\u201CIs heavy rain expected during my harvest?\u201D",
              "\u201CHow can I conserve water during this dry period?\u201D",
              "\u201CWhat are other farmers nearby reporting?\u201D",
            ]}
          />
          <p>
            Where appropriate, Farmers Pulse can combine AI assistance with weather information, agricultural
            references, local observations and community knowledge.
          </p>
          <p>
            Important crop, animal, chemical, financial and safety decisions should always be verified with
            qualified agricultural professionals or appropriate local authorities.
          </p>

          <SectionHead>Farmers Learning From Farmers</SectionHead>
          <p>Some of the world's most valuable agricultural knowledge has never appeared in a textbook.</p>
          <p>It passes from one generation to another.</p>
          <p>
            A farmer may know exactly when a particular tree should flower, which insects usually arrive
            before the rains, how a local seed performs during drought or which traditional technique
            protects soil during a monsoon.
          </p>
          <p>Farmers Pulse provides a place to preserve and share this knowledge.</p>
          <p>
            Farmers can ask questions, exchange ideas, discuss problems, document successful practices and
            learn from farmers in their own communities and around the world.
          </p>
          <p>
            Agricultural scientists, universities, extension specialists and researchers can also contribute
            knowledge and help explain emerging agricultural challenges.
          </p>

          <SectionHead>My Farm — Your Personal Farming Record</SectionHead>
          <p>
            Registered users can eventually use <em>My Farm</em> as their personal farming journal and
            management area.
          </p>
          <p>
            Farmers can record their crops, fields, planting dates, rainfall, irrigation, fertilizer
            applications, photographs, pest problems, expenses, harvest dates, yields and other observations.
          </p>
          <p>Year after year, this creates something extremely valuable:</p>
          <p className="font-serif-display text-xl md:text-2xl italic text-center text-[color:var(--np-brand,#B85F2A)]">
            the history of your own farm.
          </p>
          <p>
            Instead of relying entirely on memory, farmers can compare seasons and ask questions such as:
          </p>
          <BulletList
            items={[
              "When did we plant this crop last year?",
              "How much rainfall did we receive?",
              "When did flowering begin?",
              "Which pest appeared?",
              "What treatment was used?",
              "What was the final yield?",
            ]}
          />
          <p>That information belongs first and foremost to the farmer who created it.</p>

          <SectionHead>Farming in a Changing Climate</SectionHead>
          <p>Farmers are among the first people to experience environmental change.</p>
          <BulletList
            items={[
              "Rain arrives too early—or too late.",
              "Temperatures rise.",
              "Traditional planting seasons shift.",
              "Flooding occurs where it rarely happened before.",
              "Water sources decline.",
              "New pests appear.",
              "Pollinators change.",
              "Flowering and fruiting happen at unexpected times.",
            ]}
          />
          <p>
            A single observation may seem insignificant. Thousands of observations collected over many years
            can tell a much larger story.
          </p>
          <p>
            Farmers Pulse therefore connects agriculture with the broader mission of NaturePulse: documenting
            the pulse of our living planet.
          </p>

          <SectionHead>Citizen Science From the Farm</SectionHead>
          <p>
            With appropriate privacy protections and responsible data practices, aggregated Farmers Pulse
            observations could eventually provide useful information for students, agricultural researchers,
            universities, environmental organizations and governments.
          </p>
          <p>
            Farmer-contributed observations could help researchers better understand changing planting
            seasons, crop distribution, flowering and fruiting patterns, pest movement, rainfall variation,
            drought, flooding, soil conditions, biodiversity and the effects of climate change.
          </p>
          <p>The farmer becomes more than a recipient of agricultural information.</p>
          <p className="font-serif-display italic text-lg">
            The farmer becomes a contributor to our understanding of the planet.
          </p>

          <SectionHead>Connecting Farming, Food and Nature</SectionHead>
          <p>A farm does not exist separately from nature.</p>
          <p>
            Soil, rainfall, insects, birds, forests, rivers, groundwater, microorganisms, temperature and
            human activity are interconnected.
          </p>
          <p>
            Farmers Pulse brings agriculture into the larger NaturePulse ecosystem so that farming
            observations can contribute to a broader picture of environmental change.
          </p>
          <p>
            In time, agricultural production and surplus information may also connect with other appropriate
            platforms and communities, helping shorten the distance between farmer → food → community and
            potentially reducing unnecessary food loss and waste.
          </p>

          <SectionHead>A Platform for Every Farmer</SectionHead>
          <p>
            Farmers Pulse is intended for everyone—from someone tending a small vegetable garden to families
            cultivating a few acres, livestock farmers, fishermen, orchard owners, commercial growers,
            agricultural cooperatives and large farms.
          </p>
          <p>A farmer should not need advanced technology or scientific training to participate.</p>
          <p>If you can observe what is happening around you, you can contribute.</p>
          <p className="font-serif-display text-xl italic text-center text-[color:var(--np-brand,#B85F2A)]">
            See it. Photograph it. Record it. Share it.
          </p>

          <SectionHead>Join Farmers Pulse</SectionHead>
          <p>The world's farms are changing every day.</p>
          <p>Some changes happen slowly over decades. Others can happen overnight.</p>
          <p>By documenting what we see today, we create knowledge that may help someone tomorrow.</p>
          <p>
            We invite farmers, gardeners, agricultural professionals, students, researchers and nature
            enthusiasts everywhere to participate in Farmers Pulse within NaturePulse.
          </p>
          <BulletList
            items={[
              "Document your crops.",
              "Record your seasons.",
              "Share what you have learned.",
              "Report what is changing.",
              "Learn from farmers around the world.",
            ]}
          />
          <p>
            Together, we can create a continuously growing agricultural almanac built not only from forecasts
            and historical records, but from the observations of the people who know the land best.
          </p>
          <p className="font-serif-display italic text-center text-lg">The farmers themselves.</p>

          {/* Closing card */}
          <div className="mt-10 rounded-3xl border border-black/10 bg-[#1B4332] text-[#F5EBDC] p-8 text-center">
            <div className="font-mono-tech text-[10px] tracking-[0.4em] uppercase text-[color:var(--np-brand,#B85F2A)]">
              Farmers Pulse
            </div>
            <div className="mt-3 font-serif-display text-2xl md:text-3xl">
              Observe · Record · Learn · Share · Grow
            </div>
            <p className="mt-3 text-sm text-[#E5D8C4] max-w-lg mx-auto">
              A living agricultural almanac for a changing world — powered by farmers, communities, nature and
              knowledge.
            </p>
            <p className="mt-4 font-mono-tech text-[10px] tracking-[0.4em] uppercase text-[#3A3830]">
              Farmers Pulse — part of NaturePulse.
            </p>
            <div className="mt-6 flex flex-wrap justify-center gap-2">
              <Link
                to="/farmers/mine"
                data-testid="farmers-about-cta-mine"
                className="rounded-full bg-[color:var(--np-brand,#B85F2A)] text-white px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:opacity-90"
              >
                Set up My Farm
              </Link>
              <Link
                to="/farmers/report"
                data-testid="farmers-about-cta-report"
                className="rounded-full border border-white/25 bg-white/10 text-[#F5EBDC] px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:bg-white/20"
              >
                Report an observation
              </Link>
            </div>
          </div>
        </article>
      </div>
    </main>
  );
}

const SectionHead = ({ children }) => (
  <h2 className="mt-8 font-serif-display text-2xl md:text-3xl leading-tight text-[#1B4332]">{children}</h2>
);

const BulletList = ({ items }) => (
  <ul className="list-disc pl-6 space-y-1.5 text-[#2A2724]">
    {items.map((it, i) => (
      <li key={i}>{it}</li>
    ))}
  </ul>
);
